import React, { useState } from 'react'
import { FeedbackType } from 'interfaces/chat.interface'
import Button from 'components/button/button'

interface MessageFeedbackProps {
  messageId: string
  currentFeedback: FeedbackType | null
  onFeedbackChange: (
    messageId: string,
    feedbackType: FeedbackType,
    reason: string | null
  ) => Promise<void>
  className?: string
}

const MessageFeedback: React.FC<MessageFeedbackProps> = ({
  messageId,
  currentFeedback,
  onFeedbackChange,
  className = '',
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [feedbackText, setFeedbackText] = useState('')
  const [isFeedbackNeeded, setIsFeedbackNeeded] = useState(false)

  const handleClick = async (
    feedbackType: FeedbackType,
    reason: string | null
  ) => {
    if (isSubmitting || currentFeedback === feedbackType) return

    setIsSubmitting(true)
    try {
      await onFeedbackChange(messageId, feedbackType, reason)
    } finally {
      setIsSubmitting(false)
      setIsFeedbackNeeded(false)
      setFeedbackText('')
    }
  }

  const handleThumbsDownClick = () => {
    setIsFeedbackNeeded(true)
  }

  const handleSubmitFeedback = () => {
    if (!feedbackText.trim()) return
    handleClick('thumbs_down', feedbackText)
  }

  return (
    <div className="flex flex-col gap-2 w-full">
      <div className={`flex items-center gap-2 ${className}`}>
        <button
          onClick={() => handleClick('thumbs_up', null)}
          disabled={isSubmitting || currentFeedback !== null}
          className={`
            flex items-center justify-center w-6 h-6 rounded-full
            focus:outline-none focus:ring-2 focus:ring-green focus:ring-opacity-50
            ${isSubmitting ? 'opacity-50 cursor-not-allowed' : ''}
          `}
          aria-label="Thumbs up"
          title={
            currentFeedback === 'thumbs_up'
              ? 'You liked this response'
              : 'Like this response'
          }
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            stroke-width="1.5"
            className={`w-4 h-4 ${
              currentFeedback === 'thumbs_up'
                ? 'stroke-gray cursor-not-allowed'
                : currentFeedback !== null
                ? 'cursor-not-allowed stroke-[#C2C2C2]'
                : 'stroke-[#C2C2C2] hover:stroke-gray ease-in-out duration-200'
            }`}
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              d="M6.633 10.25c.806 0 1.533-.446 2.031-1.08a9.041 9.041 0 0 1 2.861-2.4c.723-.384 1.35-.956 1.653-1.715a4.498 4.498 0 0 0 .322-1.672V2.75a.75.75 0 0 1 .75-.75 2.25 2.25 0 0 1 2.25 2.25c0 1.152-.26 2.243-.723 3.218-.266.558.107 1.282.725 1.282m0 0h3.126c1.026 0 1.945.694 2.054 1.715.045.422.068.85.068 1.285a11.95 11.95 0 0 1-2.649 7.521c-.388.482-.987.729-1.605.729H13.48c-.483 0-.964-.078-1.423-.23l-3.114-1.04a4.501 4.501 0 0 0-1.423-.23H5.904m10.598-9.75H14.25M5.904 18.5c.083.205.173.405.27.602.197.4-.078.898-.523.898h-.908c-.889 0-1.713-.518-1.972-1.368a12 12 0 0 1-.521-3.507c0-1.553.295-3.036.831-4.398C3.387 9.953 4.167 9.5 5 9.5h1.053c.472 0 .745.556.5.96a8.958 8.958 0 0 0-1.302 4.665c0 1.194.232 2.333.654 3.375Z"
            />
          </svg>
        </button>

        <button
          onClick={handleThumbsDownClick}
          disabled={isSubmitting || currentFeedback !== null}
          className={`
            flex items-center justify-center w-6 h-6 rounded-full
            focus:outline-none focus:ring-2 focus:ring-red focus:ring-opacity-50
            ${isSubmitting ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
          `}
          aria-label="Thumbs down"
          title={
            currentFeedback === 'thumbs_down'
              ? 'You disliked this response'
              : 'Dislike this response'
          }
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            stroke-width="1.5"
            className={`w-4 h-4 ${
              currentFeedback === 'thumbs_down' || isFeedbackNeeded
                ? 'stroke-gray cursor-not-allowed'
                : currentFeedback !== null
                ? 'cursor-not-allowed stroke-[#C2C2C2]'
                : 'stroke-[#C2C2C2] hover:stroke-gray ease-in-out duration-200 '
            }`}
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              d="M7.498 15.25H4.372c-1.026 0-1.945-.694-2.054-1.715a12.137 12.137 0 0 1-.068-1.285c0-2.848.992-5.464 2.649-7.521C5.287 4.247 5.886 4 6.504 4h4.016a4.5 4.5 0 0 1 1.423.23l3.114 1.04a4.5 4.5 0 0 0 1.423.23h1.294M7.498 15.25c.618 0 .991.724.725 1.282A7.471 7.471 0 0 0 7.5 19.75 2.25 2.25 0 0 0 9.75 22a.75.75 0 0 0 .75-.75v-.633c0-.573.11-1.14.322-1.672.304-.76.93-1.33 1.653-1.715a9.04 9.04 0 0 0 2.86-2.4c.498-.634 1.226-1.08 2.032-1.08h.384m-10.253 1.5H9.7m8.075-9.75c.01.05.027.1.05.148.593 1.2.925 2.55.925 3.977 0 1.487-.36 2.89-.999 4.125m.023-8.25c-.076-.365.183-.75.575-.75h.908c.889 0 1.713.518 1.972 1.368.339 1.11.521 2.287.521 3.507 0 1.553-.295 3.036-.831 4.398-.306.774-1.086 1.227-1.918 1.227h-1.053c-.472 0-.745-.556-.5-.96a8.95 8.95 0 0 0 .303-.54"
            />
          </svg>
        </button>

        {isSubmitting && (
          <div className="flex items-center">
            <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-gray-400"></div>
          </div>
        )}
      </div>

      {/* Feedback text area */}
      {isFeedbackNeeded && (
        <div className="flex flex-col items-center gap-2">
          <textarea
            className="w-full text-sm border-[#D9D9D9] px-3 py-3 rounded-md focus:outline-none focus:border-[#D9D9D9] focus:ring-0 active:outline-none placeholder:text-[#1A374C] placeholder:opacity-50"
            rows={2}
            placeholder="Got it, help us understand how we can improve..."
            onChange={e => setFeedbackText(e.target.value)}
          />
          <div className="flex items-center gap-2 w-full">
            <Button
              variant="outline"
              className="w-full"
              onClick={() => setIsFeedbackNeeded(false)}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              className="w-full"
              onClick={handleSubmitFeedback}
              disabled={!feedbackText.trim()}
            >
              Submit
            </Button>
          </div>
        </div>
      )}
    </div>
  )
}

export default MessageFeedback
